<template>
  <div class="workflow-file-upload">
    <div v-if="shouldShowFileSelector(input)" class="mb-2">
      <!-- 文件预览区域 -->
      <div
        class="file-preview-wrapper"
        :class="{ 'edit-mode': isEditMode }"
        @contextmenu.prevent="handleContextMenu"
        @paste="handlePaste"
      >
        <!-- 没有文件时显示上传占位符（编辑模式下不显示） -->
        <div
          v-if="!inputValue && !isEditMode"
          class="file-upload-placeholder"
          :class="{ 'has-hover': hoveredUploadInputId === input.id }"
          tabindex="0"
          @mouseenter="emit('update:hovered-upload-input-id', input.id)"
          @mouseleave="emit('update:hovered-upload-input-id', null)"
          @click="triggerFileUpload()"
          @focus="handleFocus"
          @blur="handleBlur"
        >
          <!-- 上传中遮罩（图片/占位符区域内） -->
          <Transition name="fade">
            <div v-if="fileMetadata?.isUploading" class="uploading-overlay">
              <ProgressSpinner
                class="uploading-overlay__spinner"
                stroke-width="4"
                animation-duration="0.8s"
              />
              <p class="uploading-overlay__text">
                {{ $t('workflowApps.uploading') }}
                <span class="uploading-overlay__progress">
                  {{ getUploadProgress() }}%
                </span>
              </p>
            </div>
          </Transition>

          <!-- 始终显示加号 -->
          <div class="upload-placeholder-icon">
            <i class="pi pi-plus"></i>
          </div>
          <!-- 右下角素材库按钮 -->
          <Button
            :icon="
              input.type === 'file.audio'
                ? 'pi pi-volume-up'
                : input.type === 'file.video'
                  ? 'pi pi-video'
                  : 'pi pi-image'
            "
            size="small"
            text
            class="material-library-btn"
            :title="$t('workflowApps.select')"
            @click.stop="openFileSelector()"
          />
        </div>

        <!-- 有文件时显示预览 -->
        <div
          v-else
          class="file-preview-container"
          :class="{
            'audio-container': input.type === 'file.audio',
            'video-container': input.type === 'file.video'
          }"
        >
          <template v-if="inputValue">
            <!-- 图片预览 -->
            <div
              v-if="input.type === 'file.image'"
              class="image-preview"
              @mouseenter="emit('update:hovered-preview-input-id', input.id)"
              @mouseleave="emit('update:hovered-preview-input-id', null)"
            >
              <!-- 上传中遮罩（图片预览区域内） -->
              <Transition name="fade">
                <div v-if="fileMetadata?.isUploading" class="uploading-overlay">
                  <ProgressSpinner
                    class="uploading-overlay__spinner"
                    stroke-width="4"
                    animation-duration="0.8s"
                  />
                  <p class="uploading-overlay__text">
                    {{ $t('workflowApps.uploading') }}
                    <span class="uploading-overlay__progress">
                      {{ getUploadProgress() }}%
                    </span>
                  </p>
                </div>
              </Transition>

              <img
                :src="imageUrl"
                :alt="inputValue"
                class="thumbnail-image"
                @click="openImageViewer()"
                @error="handleImageError"
              />
              <!-- 右上角删除按钮 -->
              <Button
                icon="pi pi-times"
                size="small"
                text
                rounded
                severity="danger"
                class="preview-action-btn preview-delete-btn"
                :title="$t('g.delete')"
                :disabled="isEditMode"
                @click.stop="clearFileValue()"
              />
              <!-- 右下角编辑按钮（编辑模式下隐藏） -->
              <Button
                v-if="!isEditMode"
                icon="pi pi-pencil"
                size="small"
                text
                class="preview-action-btn preview-crop-btn"
                :title="$t('workflowApps.editImage')"
                @click.stop="openCropExistingImage()"
              />
            </div>
            <!-- 音频预览 -->
            <div
              v-else-if="input.type === 'file.audio'"
              class="audio-preview"
              @mouseenter="emit('update:hovered-preview-input-id', input.id)"
              @mouseleave="emit('update:hovered-preview-input-id', null)"
            >
              <!-- 上传中遮罩（音频区域内，全宽即可） -->
              <Transition name="fade">
                <div v-if="fileMetadata?.isUploading" class="uploading-overlay">
                  <ProgressSpinner
                    class="uploading-overlay__spinner"
                    stroke-width="4"
                    animation-duration="0.8s"
                  />
                  <p class="uploading-overlay__text">
                    {{ $t('workflowApps.uploading') }}
                    <span class="uploading-overlay__progress">
                      {{ getUploadProgress() }}%
                    </span>
                  </p>
                </div>
              </Transition>

              <!-- 文件名显示 -->
              <div
                v-if="fileMetadata?.originalFilename"
                class="file-name-display mb-2 text-xs text-surface-600 truncate"
                :title="fileMetadata.originalFilename"
              >
                <i class="pi pi-file-audio mr-1"></i>
                {{ fileMetadata.originalFilename }}
              </div>
              <audio :src="fileUrl" controls class="w-full" />
              <!-- 右上角删除按钮 -->
              <Button
                icon="pi pi-times"
                size="small"
                text
                rounded
                severity="danger"
                class="preview-action-btn preview-delete-btn"
                :title="$t('g.delete')"
                :disabled="isEditMode"
                @click.stop="clearFileValue()"
              />
              <!-- 右下角裁剪按钮（编辑模式下隐藏） -->
              <Button
                v-if="!isEditMode"
                icon="pi pi-pencil"
                size="small"
                text
                class="preview-action-btn preview-crop-btn"
                :title="$t('workflowApps.trimAudio')"
                @click.stop="openTrimExistingAudio()"
              />
            </div>
            <!-- 视频预览 -->
            <div
              v-else-if="input.type === 'file.video'"
              class="video-preview"
              @mouseenter="emit('update:hovered-preview-input-id', input.id)"
              @mouseleave="emit('update:hovered-preview-input-id', null)"
            >
              <Transition name="fade">
                <div v-if="fileMetadata?.isUploading" class="uploading-overlay">
                  <ProgressSpinner
                    class="uploading-overlay__spinner"
                    stroke-width="4"
                    animation-duration="0.8s"
                  />
                  <p class="uploading-overlay__text">
                    {{ $t('workflowApps.uploading') }}
                    <span class="uploading-overlay__progress">
                      {{ getUploadProgress() }}%
                    </span>
                  </p>
                </div>
              </Transition>

              <video
                :src="fileMetadata?.previewUrl || fileUrl"
                controls
                class="video-player"
              />
              <Button
                icon="pi pi-times"
                size="small"
                text
                rounded
                severity="danger"
                class="preview-action-btn preview-delete-btn"
                :title="$t('g.delete')"
                :disabled="isEditMode"
                @click.stop="clearFileValue()"
              />
              <Button
                v-if="!isEditMode"
                icon="pi pi-pencil"
                size="small"
                text
                class="preview-action-btn preview-crop-btn"
                :title="$t('workflowApps.trimVideo')"
                @click.stop="openTrimExistingVideo()"
              />
            </div>
          </template>
        </div>
      </div>

      <!-- 隐藏的文件输入 -->
      <input
        ref="fileInputRef"
        :data-input-id="input.id"
        type="file"
        :accept="
          input.type === 'file.audio'
            ? 'audio/*'
            : input.type === 'file.image'
              ? 'image/*,video/*'
              : input.type === 'file.video'
                ? 'video/*'
                : 'image/*'
        "
        style="display: none"
        @change="handleFileUpload"
      />
    </div>

    <!-- 右键菜单 -->
    <ContextMenu
      v-if="input.type === 'file.image' && !isEditMode"
      ref="contextMenuRef"
      :model="contextMenuItems"
    />
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import ContextMenu from 'primevue/contextmenu'
import ProgressSpinner from 'primevue/progressspinner'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface WorkflowInput {
  id: string
  name: string
  type: string
  value: any
  description?: string
  placeholder?: string
  options?: any[]
  order?: number
  config: {
    min?: number
    max?: number
    step?: number
    defaultValue?: any
    required?: boolean
    order?: number
    llmPrompt?: string
    [key: string]: any
  }
}

type FileMetadataItem = {
  name?: string
  obsUrl?: string
  originalFilename?: string
  fileId?: number
  previewUrl?: string
  isUploading?: boolean
}

const props = withDefaults(
  defineProps<{
    input: WorkflowInput
    inputValue?: any
    fileMetadata?: FileMetadataItem
    uploadProgress?: number
    isEditMode?: boolean
    imageUrl?: string
    fileUrl?: string
    hoveredUploadInputId?: string | null
    hoveredPreviewInputId?: string | null
  }>(),
  {
    inputValue: '',
    uploadProgress: 0,
    isEditMode: false,
    imageUrl: '',
    fileUrl: '',
    hoveredUploadInputId: null,
    hoveredPreviewInputId: null
  }
)

const emit = defineEmits<{
  'file-upload': [event: Event]
  'open-file-selector': []
  'trigger-file-upload': []
  'clear-file-value': []
  'open-image-viewer': []
  'open-crop-existing-image': []
  'open-trim-existing-audio': []
  'open-trim-existing-video': []
  'update:hovered-upload-input-id': [value: string | null]
  'update:hovered-preview-input-id': [value: string | null]
  'paste-image': [file: File]
}>()

const { t } = useI18n()
const fileInputRef = ref<HTMLInputElement>()
const contextMenuRef = ref<InstanceType<typeof ContextMenu>>()

const shouldShowFileSelector = (input: WorkflowInput): boolean => {
  return (
    input.type === 'file.image' ||
    input.type === 'file.audio' ||
    input.type === 'file.video'
  )
}

const triggerFileUpload = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click()
  }
}

const handleFileUpload = (event: Event) => {
  emit('file-upload', event)
}

const openFileSelector = () => {
  emit('open-file-selector')
}

const clearFileValue = () => {
  emit('clear-file-value')
}

const openImageViewer = () => {
  emit('open-image-viewer')
}

const openCropExistingImage = () => {
  emit('open-crop-existing-image')
}

const openTrimExistingAudio = () => {
  emit('open-trim-existing-audio')
}

const openTrimExistingVideo = () => {
  emit('open-trim-existing-video')
}

const getUploadProgress = () => {
  return Math.max(0, Math.min(100, Math.round(props.uploadProgress)))
}

const handleImageError = () => {
  console.warn('图片加载失败')
}

// 右键菜单项
const contextMenuItems = computed(() => {
  if (props.input.type !== 'file.image' || props.isEditMode) {
    return []
  }
  return [
    {
      label: t('workflowApps.pasteImage'),
      icon: 'pi pi-paste',
      command: () => {
        void handlePasteImage()
      }
    }
  ]
})

// 处理右键菜单
const handleContextMenu = (event: MouseEvent) => {
  if (props.input.type === 'file.image' && !props.isEditMode) {
    contextMenuRef.value?.show(event)
  }
}

// 处理粘贴事件（键盘快捷键 Ctrl+V）
const handlePaste = async (event: ClipboardEvent) => {
  if (props.input.type !== 'file.image' || props.isEditMode) {
    return
  }

  const items = event.clipboardData?.items
  if (!items) {
    return
  }

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.type.indexOf('image') !== -1) {
      event.preventDefault()
      const file = item.getAsFile()
      if (file) {
        emit('paste-image', file)
      }
      break
    }
  }
}

// 处理从右键菜单触发的粘贴
const handlePasteImage = async () => {
  try {
    const clipboardItems = await navigator.clipboard.read()
    for (const clipboardItem of clipboardItems) {
      for (const type of clipboardItem.types) {
        if (type.startsWith('image/')) {
          const blob = await clipboardItem.getType(type)
          const file = new File(
            [blob],
            `pasted-image-${Date.now()}.${type.split('/')[1]}`,
            {
              type
            }
          )
          emit('paste-image', file)
          return
        }
      }
    }
  } catch (error) {
    console.warn('无法从剪贴板读取图片:', error)
  }
}

// 监听全局粘贴事件（当焦点在图片上传框时）
let pasteHandler: ((e: ClipboardEvent) => void) | null = null
let isFocused = ref(false)

const handleFocus = () => {
  if (props.input.type === 'file.image' && !props.isEditMode) {
    isFocused.value = true
  }
}

const handleBlur = () => {
  isFocused.value = false
}

onMounted(() => {
  if (props.input.type === 'file.image' && !props.isEditMode) {
    pasteHandler = (e: ClipboardEvent) => {
      // 检查是否在图片上传框区域内或已获得焦点
      const target = e.target as HTMLElement
      const wrapper = target.closest('.file-preview-wrapper')
      if (wrapper || isFocused.value) {
        void handlePaste(e)
      }
    }
    document.addEventListener('paste', pasteHandler)
  }
})

onUnmounted(() => {
  if (pasteHandler) {
    document.removeEventListener('paste', pasteHandler)
  }
})
</script>

<style scoped>
/* 文件上传相关样式 */
.file-preview-wrapper {
  position: relative;
  margin-bottom: 0.5rem;
  width: 100%;
}

/* 仅限制图片预览及上传占位宽度，不影响音频播放器宽度 */
.file-preview-wrapper:has(.image-preview) {
  max-width: 150px;
}

.file-preview-wrapper:has(.file-upload-placeholder):not(
    :has(.audio-preview)
  ):not(:has(.video-preview)) {
  max-width: 150px;
}

.file-preview-wrapper:has(
    .file-preview-container:not(.audio-container):not(.video-container)
  ) {
  aspect-ratio: 1;
}

/* 编辑模式下缩小预览大小 */
.file-preview-wrapper.edit-mode {
  max-width: 120px;
}

.file-preview-wrapper.edit-mode:has(
    .file-preview-container:not(.audio-container)
  ) {
  max-width: 120px;
  width: 120px;
}

.file-preview-container {
  position: relative;
  border: none;
  border-radius: 0.75rem;
  padding: 0;
  background-color: transparent;
  width: 100%;
  aspect-ratio: 1;
}

.file-preview-container.audio-container {
  min-height: auto;
  height: auto;
  aspect-ratio: auto;
}

.file-preview-container.video-container {
  min-height: auto;
  height: auto;
  aspect-ratio: auto;
}

.image-preview {
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
}

.image-preview .thumbnail-image {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  max-width: 150px;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  display: block;
}

.image-preview .thumbnail-image:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.audio-preview {
  position: relative;
  width: 100%;
}

.video-preview {
  position: relative;
  width: 100%;
}

.video-player {
  width: 100%;
  max-height: 240px;
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
  background-color: #000;
  object-fit: contain;
}

.file-name-display {
  padding: 0.5rem;
  background: rgba(var(--comfy-menu-secondary-bg-rgb, 42, 42, 42), 0.5);
  border-radius: 0.375rem;
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  max-width: 100%;
  word-break: break-all;
}

.file-name-display i {
  flex-shrink: 0;
  color: var(--p-primary-color);
}

.file-upload-placeholder {
  position: relative;
  aspect-ratio: 1;
  width: 100%;
  border: 2px dashed var(--border-color, #6b7280);
  border-radius: 0.75rem;
  background-color: rgba(var(--comfy-menu-secondary-bg-rgb, 42, 42, 42), 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.file-upload-placeholder:hover,
.file-upload-placeholder.has-hover {
  background-color: rgba(107, 114, 128, 0.1);
  border-color: var(--border-color, #6b7280);
}

.file-upload-placeholder:focus {
  outline: 2px solid var(--p-primary-color, #667eea);
  outline-offset: 2px;
  border-color: var(--p-primary-color, #667eea);
}

.file-upload-placeholder:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.upload-placeholder-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  color: var(--text-color-secondary, #9ca3af);
  transition: all 0.2s ease;
}

.file-upload-placeholder:hover .upload-placeholder-icon,
.file-upload-placeholder.has-hover .upload-placeholder-icon {
  color: var(--text-color-secondary, #9ca3af);
}

.upload-placeholder-icon i {
  font-size: 2rem;
}

.material-library-btn {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  z-index: 10;
  background: rgba(0, 0, 0, 0.6) !important;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  transition:
    background 0.3s ease,
    transform 0.3s ease;
  padding: 0.5rem !important;
  min-width: auto !important;
  width: auto !important;
}

.material-library-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.8) !important;
  transform: scale(1.05);
}

.material-library-btn :deep(.p-button-icon) {
  color: white !important;
  margin: 0 !important;
}

.material-library-btn :deep(.p-button-label) {
  display: none !important;
}

.preview-action-btn {
  position: absolute;
  z-index: 10;
  background: rgba(0, 0, 0, 0.7) !important;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    background 0.2s ease;
  opacity: 0;
  pointer-events: none;
}

.image-preview:hover .preview-action-btn,
.audio-preview:hover .preview-action-btn,
.video-preview:hover .preview-action-btn {
  opacity: 1;
  pointer-events: auto;
}

.preview-action-btn:hover {
  background: rgba(0, 0, 0, 0.85) !important;
  transform: scale(1.05);
}

.preview-delete-btn {
  top: -1.5rem;
  right: -0.5rem;
  background: rgba(239, 68, 68, 0.9) !important;
}

.preview-delete-btn:hover:not(:disabled) {
  background: rgba(239, 68, 68, 1) !important;
}

.preview-crop-btn {
  top: -1.25rem;
  right: 2.5rem;
}

.preview-action-btn :deep(.p-button-icon) {
  color: white !important;
  margin: 0 !important;
}

.preview-action-btn :deep(.p-button-label) {
  display: none !important;
}

.preview-action-btn :deep(.p-button) {
  padding: 0.5rem !important;
  min-width: auto !important;
  width: auto !important;
}

.uploading-overlay {
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 0.75rem;
  text-align: center;
  color: #fff;
  backdrop-filter: blur(2px);
  pointer-events: none;
}

.uploading-overlay__spinner {
  width: 2.5rem;
  height: 2.5rem;
}

.uploading-overlay__spinner :deep(svg) {
  width: 100%;
  height: 100%;
}

.uploading-overlay__text {
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}

.uploading-overlay__progress {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  opacity: 0.9;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
